import { memo, useState, useCallback, useEffect } from "react";
import { IconX, IconArrowLeft, IconUser, IconNotes, IconHistory, IconBriefcase } from "@tabler/icons-react";
import ContactInfo from "./ContactInfo";
import NotesSection from "./NotesSection";
import SessionHistory from "./SessionHistory";
import CRMTab from "./CRMTab";
import { useServiceCenter } from "../../contexts";
import "./ContactPanel.css";

/**
 * Painel lateral com informações do contato
 */
const ContactPanel = memo(({ isOpen, onClose }) => {
  const { selectedConversation, selectedContact } = useServiceCenter();
  const [activeTab, setActiveTab] = useState("crm");

  const tabs = [
    // { id: "info", label: "Contato", icon: <IconUser size={20} /> },
    { id: "crm", label: "CRM", icon: <IconBriefcase size={20} /> },
    { id: "notes", label: "Notas", icon: <IconNotes size={20} /> },
    { id: "history", label: "Histórico", icon: <IconHistory size={20} /> },
  ];

  // Handler para fechar o painel
  const handleClose = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  }, [onClose]);

  // Handler para fechar o painel clicando no backdrop
  const handleBackdropClick = useCallback((e) => {
    if (e.target.classList.contains("sc-contact-panel__backdrop")) {
      onClose();
    }
  }, [onClose]);

  // Prevenir scroll do body quando o painel está aberto no mobile
  useEffect(() => {
    if (isOpen && window.innerWidth <= 768) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const renderContent = () => {
    switch (activeTab) {
      case "info":
        return (
          <ContactInfo
            contact={selectedContact}
            conversation={selectedConversation}
          />
        );
      case "notes":
        return (
          <NotesSection contactId={selectedConversation?.contact_id} />
        );
      case "history":
        return (
          <SessionHistory contactId={selectedConversation?.contact_id} />
        );
      case "crm":
        return (
          <CRMTab
            conversation={selectedConversation}
            contact={selectedContact}
          />
        );
      default:
        return null;
    }
  };

  if (!selectedConversation) return null;

  return (
    <>
      {/* Backdrop para mobile/tablet */}
      {isOpen && (
        <div
          className="sc-contact-panel__backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div className={`sc-contact-panel ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="sc-contact-panel__header">
          <button
            type="button"
            className="sc-contact-panel__close"
            onClick={handleClose}
            aria-label="Fechar painel"
          >
            <IconArrowLeft size={20} className="sc-contact-panel__close-icon--mobile" />
            <IconX size={20} className="sc-contact-panel__close-icon--desktop" />
          </button>
          <h3 className="sc-contact-panel__title">Detalhes do Contato</h3>
        </div>

        {/* Tabs */}
        <div className="sc-contact-panel__tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`sc-contact-panel__tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content — só renderiza quando o painel está aberto */}
        <div className="sc-contact-panel__content">
          {isOpen && renderContent()}
        </div>
      </div>
    </>
  );
});

ContactPanel.displayName = "ContactPanel";

export default ContactPanel;
